import {Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {DatePipe, Location, NgClass} from '@angular/common';
import {catchError, forkJoin, of} from 'rxjs';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {
  MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable
} from '@angular/material/table';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatFormField, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {FormsModule} from '@angular/forms';
import {BenchOverviewService} from '../../services/bench-overview.service';
import {DateService} from '../../services/date.service';
import {
  acceptedCommitmentFrom, benchCategories, BENCH_TYPES, BenchEntryDTO, BenchFirm, BenchType,
  isBenchFirm, isBenchType, RequestStatus, SalesPersonDTO
} from '../../shared/model/bench-entry.model';
import {
  BenchAvailabilityFilter, BenchFilterState, BenchFilterStateService, BenchSortDirection,
  BenchSortKey, BenchTypeFilter, BenchViewMode
} from '../../services/bench-filter-state.service';
import {MessageService} from '../../shared/components/message.service';
import {sortIndicatorFor} from '../../shared/sort-indicator';
import {CompanyService} from '../../services/company.service';
import {BeraterUebersichtService} from '../../services/beraterUebersicht.service';
import {SalesModalComponent} from './sales-modal/sales-modal.component';
import {LazyImageDirective} from '../../shared/lazy-image.directive';
import {CompanyBadgeComponent} from '../../shared/components/company-badge/company-badge.component';
import {CiSelectComponent} from '../../../profiler-theme';
import {ScrollTargetService} from '../../services/scroll-target.service';
import {AvailableFromFilterComponent} from './available-from-filter/available-from-filter.component';

type QuickFilterKey = RequestStatus;

const TYPE_FILTER_LABEL_KEYS: Record<BenchTypeFilter, string> = {
  ALL: 'benchOverview.filter.allTypes',
  CONSULTANT: 'benchOverview.filter.onlyConsultant',
  APPLICANT: 'benchOverview.filter.onlyApplicant',
  FREELANCER: 'benchOverview.filter.onlyFreelancer',
};

@Component({
  selector: 'app-bench-overview',
  imports: [
    DatePipe,
    NgClass,
    TranslatePipe,
    FormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatProgressSpinner,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MatTooltip,
    DatePipe,
    SalesModalComponent,
    LazyImageDirective,
    CompanyBadgeComponent,
    CiSelectComponent,
    AvailableFromFilterComponent,
  ],
  templateUrl: './bench-overview.component.html',
  styleUrl: './bench-overview.component.scss',
  standalone: true,
})
export class BenchOverviewComponent implements OnInit {
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dateService = inject(DateService);
  private readonly companyService = inject(CompanyService);
  private readonly userOverviewService = inject(BeraterUebersichtService);
  private readonly translate = inject(TranslateService);
  private readonly filterState = inject(BenchFilterStateService);
  private readonly location = inject(Location);
  private readonly scrollTarget = inject(ScrollTargetService);

  entries: BenchEntryDTO[] = [];
  filteredEntries: BenchEntryDTO[] = [];
  salesPersons: SalesPersonDTO[] = [];
  isLoading = true;
  viewMode: BenchViewMode = 'table';
  salesModalOpen = false;

  searchText = '';
  selectedCompany: 'ALL' | BenchFirm = 'ALL';
  selectedType: BenchTypeFilter = 'ALL';
  typeFilterOptions: BenchTypeFilter[] = [];
  private typeFilterLabels = new Map<string, string>();
  private typeSortLabels = new Map<BenchType, string>();

  sortKey: BenchSortKey = 'person';
  sortDir: BenchSortDirection = 'asc';
  activeQuickFilters = new Set<QuickFilterKey>();
  availability: BenchAvailabilityFilter = 'NOW';
  availableFrom: string | null = null;

  private static readonly MIN_VISIBLE_ROWS = 5;

  @ViewChild(AvailableFromFilterComponent) private availableFromFilter?: AvailableFromFilterComponent;

  get hasNonDefaultQuickFilter(): boolean {
    return this.activeQuickFilters.size > 0 || this.availability !== 'NOW' || !!this.availableFrom;
  }

  @ViewChild('entriesScroll')
  set entriesScroll(area: ElementRef<HTMLElement> | undefined) {
    this.scrollTarget.register(area?.nativeElement ?? null);
  }

  get visibleRowCount(): number {
    return Math.min(this.filteredEntries.length, BenchOverviewComponent.MIN_VISIBLE_ROWS);
  }

  readonly displayedColumns = [
    'person', 'type', 'company', 'position',
    'location', 'availableFrom', 'confirmedFrom',
    'vacation', 'offers',
  ];

  displayTypeFilter = (value: string): string => this.typeFilterLabels.get(value) ?? value;

  /** Template-Guard: Typ-Badge nur bei bekanntem Typ rendern (NP20-871). */
  protected readonly isBenchType = isBenchType;

  /** Template-Guard: Firma-Badge nur bei bekannter Firma rendern (NP20-877). */
  protected readonly isBenchFirm = isBenchFirm;

  get companies(): BenchFirm[] {
    return this.companyService.companies().map(c => c.abbreviation);
  }

  companyName(abbreviation: string | null | undefined): string {
    return this.companyService.byAbbreviation(abbreviation ?? undefined)?.name ?? (abbreviation ?? '');
  }

  firmColor(abbreviation: string | null | undefined): string | null {
    return this.companyService.byAbbreviation(abbreviation ?? undefined)?.color ?? null;
  }

  chipStyle(abbreviation: string, active: boolean): Record<string, string> {
    if (!active) return {};
    const safe = (this.companyService.byAbbreviation(abbreviation)?.color || '').trim();
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(safe)) return {};
    return {
      background: `color-mix(in srgb, ${safe} 12%, white)`,
      color: safe,
      'border-color': `color-mix(in srgb, ${safe} 45%, white)`,
    };
  }
  readonly statusFilters: { key: QuickFilterKey; labelKey: string }[] = [
    {key: 'OPEN', labelKey: 'benchOverview.quickFilter.open'},
    {key: 'FEEDBACK', labelKey: 'benchOverview.quickFilter.feedback'},
    {key: 'ACCEPTED', labelKey: 'benchOverview.quickFilter.confirmed'},
    {key: 'PROJECT_CREATED', labelKey: 'benchOverview.quickFilter.project_created'},
    {key: 'REJECTED', labelKey: 'benchOverview.quickFilter.rejected'},
    {key: 'CLOSED', labelKey: 'benchOverview.quickFilter.closed'},
  ];

  readonly availabilityOptions: { key: BenchAvailabilityFilter; labelKey: string }[] = [
    {key: 'NOW', labelKey: 'benchOverview.availability.now'},
    {key: 'SOON', labelKey: 'benchOverview.availability.soon'},
    {key: 'ALL', labelKey: 'benchOverview.availability.all'},
  ];

  private static readonly URL_WRITE_DELAY_MS = 300;
  private urlWriteTimer?: number;
  private overviewPath = '';

  ngOnInit(): void {
    this.overviewPath = this.pathOf(this.location.path(true));
    this.destroyRef.onDestroy(() => window.clearTimeout(this.urlWriteTimer));
    this.destroyRef.onDestroy(() => this.scrollTarget.register(null));
    this.restoreFilterState();
    this.buildFilterOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildFilterOptions());
    // Deep-link opener: react to ?sales=1 even if the param arrives after init.
    // Open-only — closing is driven by the modal's visibleChange (which strips the param).
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params.get('sales') === '1') this.salesModalOpen = true;
      });
    forkJoin({
      companies: this.companyService.load(),
      entries: this.benchService.getEntries(),
      sales: this.benchService.getSales(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({entries, sales}) => {
          // NP20-736: Firm-Abkürzungen normalisieren, damit downstream (SalesMaps,
          // Filter-Chips) mit dem kanonischen Kürzel arbeitet.
          const normalize = (firm: BenchFirm | null | undefined): BenchFirm =>
            this.companyService.resolveAbbreviation(firm) ?? (firm ?? '');
          this.salesPersons = sales.map(s => ({...s, firm: normalize(s.firm)}));
          this.entries = entries.map(e => ({...e, firm: normalize(e.firm)}));
          this.normalizeSelectedCompany();
          this.enrichFirmFromConsultants();
          // NP20-902: filtern statt nur sortieren, sonst greifen die Standardfilter erst nach der Anreicherung.
          this.applyFilter();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.normalizeSelectedCompany();
          this.persistFilterState();
          this.messageService.error('benchOverview.loadError');
        },
      });
  }

  /** Die Adresszeile gewinnt, sonst greift die gemerkte Auswahl. */
  private restoreFilterState(): void {
    const params = this.route.snapshot.queryParamMap;
    const state = this.filterState.hasFilterParams(params)
      ? this.filterState.fromQueryParams(params)
      : this.filterState.read();
    this.searchText = state.search;
    this.selectedCompany = state.company;
    this.selectedType = state.type;
    this.availability = state.availability;
    this.availableFrom = state.availableFrom;
    this.activeQuickFilters = new Set(state.status);
    this.sortKey = state.sortKey;
    this.sortDir = state.sortDir;
    this.viewMode = state.viewMode;
    this.persistFilterState();
  }

  private currentFilterState(): BenchFilterState {
    return {
      search: this.searchText,
      company: this.selectedCompany,
      type: this.selectedType,
      availability: this.availability,
      availableFrom: this.availableFrom,
      status: [...this.activeQuickFilters],
      sortKey: this.sortKey,
      sortDir: this.sortDir,
      viewMode: this.viewMode,
    };
  }

  private persistFilterState(debounceUrl = false): void {
    const state = this.currentFilterState();
    this.filterState.write(state);
    window.clearTimeout(this.urlWriteTimer);
    if (!debounceUrl) {
      this.writeQueryParams(this.filterState.toQueryParams(state));
      return;
    }
    this.urlWriteTimer = window.setTimeout(
      () => this.writeQueryParams(this.filterState.toQueryParams(this.currentFilterState())),
      BenchOverviewComponent.URL_WRITE_DELAY_MS);
  }

  /**
   * Ohne Router: ein NavigationStart pro Filterklick raeumt die Toasts weg und laesst den
   * AuthGuard erneut laufen. Fremde Parameter wie sales=1 bleiben stehen.
   */
  private writeQueryParams(params: Params): void {
    const current = this.location.path(true);
    if (this.pathOf(current) !== this.overviewPath) return;
    const tree = this.router.parseUrl(current);
    const merged: Params = {...tree.queryParams};
    for (const [key, value] of Object.entries(params)) {
      if (value === null) delete merged[key];
      else merged[key] = value;
    }
    tree.queryParams = merged;
    const next = this.router.serializeUrl(tree);
    if (next === current) return;
    this.location.replaceState(next);
  }

  private pathOf(url: string): string {
    return url.split(/[?#]/)[0];
  }

  private normalizeSelectedCompany(): void {
    if (this.selectedCompany === 'ALL') return;
    if (!this.companies.length) return;
    const resolved = this.companyService.resolveAbbreviation(this.selectedCompany);
    if (resolved === this.selectedCompany) return;
    this.selectedCompany = resolved ?? 'ALL';
  }

  private buildFilterOptions(): void {
    this.typeFilterOptions = ['ALL', ...BENCH_TYPES];
    const typeLabels = new Map<string, string>();
    for (const value of this.typeFilterOptions) {
      typeLabels.set(value, this.translate.instant(TYPE_FILTER_LABEL_KEYS[value]));
    }
    this.typeFilterLabels = typeLabels;

    this.typeSortLabels = new Map(
      BENCH_TYPES.map(type => [type, this.translate.instant(`benchOverview.type.${type.toLowerCase()}`)]));
  }

  // Late enrichment: liest die am Benutzer gespeicherte Firma aus der Beraterübersicht
  // und überschreibt entry.firm/sales.firm, wo sie eine Abkürzung liefert — Bench-Zeilen
  // können älter sein als die letzte Firmen-Änderung. Fehler (Berechtigung, 404) werden
  // geschluckt, damit die Seite mit den BE-Werten rendert.
  private enrichFirmFromConsultants(): void {
    this.userOverviewService.query()
      .pipe(
        catchError(() => of<any>(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users: any) => {
        if (!users) return;
        const firmByUserId = new Map<string, string | null>();
        for (const u of (users.body ?? []) as {id?: string; firma?: string | null}[]) {
          if (u.id) firmByUserId.set(u.id, this.companyService.resolveAbbreviation(u.firma));
        }
        const firmOf = (id: string | null | undefined): string | null =>
          (id ? firmByUserId.get(id) : null) ?? null;
        this.entries = this.entries.map(e => {
          const fresh = firmOf(e.consultantId);
          return fresh ? {...e, firm: fresh} : e;
        });
        this.salesPersons = this.salesPersons.map(s => {
          const fresh = firmOf(s.userId);
          return fresh ? {...s, firm: fresh} : s;
        });
        this.applyFilter();
      });
  }

  openDetail(entry: BenchEntryDTO): void {
    this.router.navigate(['/berater-abfrage', entry.id]);
  }

  openSalesModal(): void {
    this.salesModalOpen = true;
  }

  openMeetingMode(): void {
    this.router.navigate(['/berater-abfrage/meeting']);
  }

  openSalesView(): void {
    this.router.navigate(['/berater-abfrage/sales']);
  }

  onSalesModalVisibleChange(open: boolean): void {
    this.salesModalOpen = open;
    // Clear the deep-link param on close so a refresh / shared link doesn't silently re-open the modal.
    if (!open) this.writeQueryParams({sales: null});
  }

  onSalesSaved(sales: SalesPersonDTO[]): void {
    this.salesPersons = sales;
    this.applyFilter();
  }

  get absentSalesCount(): number {
    return this.salesPersons.filter(s => !!s.substituteUserId && this.isAbsent(s)).length;
  }

  categoriesOf(entry: BenchEntryDTO): BenchType[] {
    return benchCategories(entry);
  }

  isApplicant(entry: BenchEntryDTO): boolean {
    return benchCategories(entry).includes('APPLICANT');
  }

  // NP20-874: Wer Bewerber und Freelancer ist, zaehlt in beiden Kategorien — die Summe kann
  // deshalb ueber der Gesamtzahl liegen, passend zu den Filtern.
  get consultantCount(): number {
    return this.countOf('CONSULTANT');
  }

  get applicantCount(): number {
    return this.countOf('APPLICANT');
  }

  get freelancerCount(): number {
    return this.countOf('FREELANCER');
  }

  private countOf(category: BenchType): number {
    return this.filteredEntries.filter(e => benchCategories(e).includes(category)).length;
  }

  isAbsent(sales: SalesPersonDTO): boolean {
    return this.dateService.isSalesMemberAbsent(sales);
  }

  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName
      .split(' ')
      .map(w => w[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /** Verfügbar-ab-Bucket: 'past' = ab heute/überfällig (rot), 'soon' = in den nächsten 2 Monaten (orange), 'later' = später (grün). */
  availabilityBucket(entry: BenchEntryDTO): 'past' | 'soon' | 'later' | null {
    if (!entry.availableFrom) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(entry.availableFrom);
    if (from <= today) return 'past';
    const horizon = new Date(today);
    horizon.setMonth(horizon.getMonth() + 2);
    return from <= horizon ? 'soon' : 'later';
  }

  /** Angebots-Kennzahlen für die Inline-Spalte „Angebot & Nachfrage". */
  // acceptedDate ist hier entfallen (NP20-797): das Feld wurde von keiner Ansicht gelesen und haette
  // mit der neuen Regel („Datum gilt ab der Zusage") eine widersprechende Bedeutung behalten.
  offerSummary(entry: BenchEntryDTO): {
    open: number; feedback: number; rejected: number; accepted: number; projectCreated: number;
    pdl: number; customer: number;
  } {
    const offers = entry.offers ?? [];
    const by = (s: RequestStatus): number => offers.filter(o => o.status === s).length;
    return {
      open: by('OPEN'),
      feedback: by('FEEDBACK'),
      rejected: by('REJECTED'),
      accepted: by('ACCEPTED'),
      // NP20-797: eigener Zaehler, sonst verschwindet ein Angebot aus der Statistik, sobald das
      // Projekt angelegt ist — es zaehlte dann weder als Zusage noch sonst irgendwo.
      projectCreated: by('PROJECT_CREATED'),
      pdl: offers.filter(o => o.interviewPdl === 'ja').length,
      customer: offers.filter(o => o.interviewCustomer === 'ja').length,
    };
  }

  /** Zusage-Datum für die "Zusage ab"-Spalte (NP20-807); Ableitung liegt im Modell, damit die Detailansicht dieselbe nutzt. */
  acceptedOfferDate(entry: BenchEntryDTO): string | null {
    return acceptedCommitmentFrom(entry);
  }

  toggleQuickFilter(key: QuickFilterKey): void {
    if (this.activeQuickFilters.has(key)) {
      this.activeQuickFilters.delete(key);
    } else {
      this.activeQuickFilters.add(key);
    }
    this.applyFilter();
  }

  setAvailability(value: BenchAvailabilityFilter): void {
    this.clearAvailableFrom();
    this.availability = value;
    this.applyFilter();
  }

  onAvailableFromChange(value: string | null): void {
    this.availableFrom = value;
    this.availability = 'NOW';
    if (value !== null) {
      this.sortKey = 'availableFrom';
      this.sortDir = 'asc';
    }
    this.applyFilter();
  }

  private clearAvailableFrom(): void {
    this.availableFrom = null;
    this.availableFromFilter?.clear();
  }

  selectCompany(value: 'ALL' | BenchFirm): void {
    this.selectedCompany = value;
    this.applyFilter();
  }

  setViewMode(value: BenchViewMode): void {
    this.viewMode = value;
    this.persistFilterState();
  }

  resetQuickFilter(): void {
    this.activeQuickFilters.clear();
    // Zurück auf den Initialzustand (Standard 'NOW'), nicht 'ALL'
    this.availability = 'NOW';
    this.clearAvailableFrom();
    this.applyFilter();
  }

  onSearchChange(): void {
    this.applyFilter(true);
  }

  applyFilter(debounceUrl = false): void {
    let result = [...this.entries];

    if (this.searchText.trim()) {
      const term = this.searchText.toLowerCase();
      result = result.filter(e =>
        e.fullName?.toLowerCase().includes(term) ||
        e.abbreviation?.toLowerCase().includes(term) ||
        e.position?.toLowerCase().includes(term)
      );
    }

    if (this.selectedCompany !== 'ALL') {
      result = result.filter(e => e.firm === this.selectedCompany);
    }

    if (this.selectedType !== 'ALL') {
      const selected = this.selectedType;
      result = result.filter(e => benchCategories(e).includes(selected));
    }

    if (this.availableFrom) {
      const from = this.availableFrom;
      result = result.filter(e => !!e.availableFrom && e.availableFrom.slice(0, 10) >= from);
    } else if (this.availability !== 'ALL') {
      result = result.filter(e => {
        const bucket = this.availabilityBucket(e);
        // NOW = bereits/überfällig verfügbar; SOON („+ 2 Monate") = das UND die nächsten 2 Monate.
        return this.availability === 'NOW' ? bucket === 'past' : bucket === 'past' || bucket === 'soon';
      });
    }

    // NP20-895: nach Angebots-Status filtern.
    if (this.activeQuickFilters.size > 0) {
      result = result.filter(e => (e.offers ?? []).some(o => this.activeQuickFilters.has(o.status)));
    }

    result = this.sortEntries(result);
    this.filteredEntries = result;
    this.persistFilterState(debounceUrl);
  }

  /** Klick auf einen Spalten-Header: gleiche Spalte -> Richtung toggeln, sonst neue Spalte aufsteigend. */
  onSort(key: BenchSortKey): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.filteredEntries = this.sortEntries(this.filteredEntries);
    this.persistFilterState();
  }

  /** aria-sort-Wert für den Header (Barrierefreiheit). */
  ariaSort(key: BenchSortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey !== key) return 'none';
    return this.sortDir === 'asc' ? 'ascending' : 'descending';
  }

  sortIndicator(key: BenchSortKey): string {
    return sortIndicatorFor(this.sortKey, key, this.sortDir);
  }

  private sortEntries(entries: BenchEntryDTO[]): BenchEntryDTO[] {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...entries].sort((a, b) => {
      const va = this.sortValue(a);
      const vb = this.sortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }

  private sortValue(entry: BenchEntryDTO): string | number {
    switch (this.sortKey) {
      case 'type':          return benchCategories(entry).map(c => this.typeSortLabels.get(c) ?? c).join(', ');
      case 'company':       return entry.firm ?? '';
      case 'position':      return entry.position ?? '';
      case 'location':      return entry.location ?? '';
      // ISO-Datumsstrings sortieren lexikografisch korrekt; leere Werte landen vorne (asc).
      case 'availableFrom': return entry.availableFrom ?? '';
      case 'confirmedFrom': return this.acceptedOfferDate(entry) ?? '';
      case 'vacation':      return entry.remainingVacationDays ?? 0;
      case 'offers':        return entry.offers?.length ?? 0;
      default:              return entry.fullName ?? '';
    }
  }

}
